import { createTheme } from '@mui/material/styles';

// Brand Colors
const BRAND_COLORS = {
  darkViolet: '#270067',    // Primary identity
  violet: '#44019A',         // Secondary identity
  lightGray: '#F4F4F4',      // Background
  pink: '#FF4672',           // Accent
  lightViolet: '#8045FF',    // Accent
};

const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: BRAND_COLORS.darkViolet,     // #270067
      light: BRAND_COLORS.lightViolet,   // #8045FF
      dark: BRAND_COLORS.darkViolet,     // #270067
    },
    secondary: {
      main: BRAND_COLORS.violet,         // #44019A
      light: BRAND_COLORS.lightViolet,   // #8045FF
      dark: BRAND_COLORS.darkViolet,     // #270067
    },
    background: {
      default: BRAND_COLORS.lightGray,   // #F4F4F4
      paper: '#ffffff',
    },
    text: {
      primary: '#111111',
      secondary: '#555555',
      disabled: '#999999',
    },
    success: {
      main: '#2E7D32',
      light: '#4CAF50',
      dark: '#1B5E20',
    },
    error: {
      main: BRAND_COLORS.pink,           // #FF4672
    },
    warning: {
      main: BRAND_COLORS.darkViolet,
    },
    info: {
      main: BRAND_COLORS.lightViolet,    // #8045FF
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: '3.5rem',
      fontWeight: 900,
      letterSpacing: '-0.02em',
      color: BRAND_COLORS.violet,
    },
    h2: {
      fontSize: '3rem',
      fontWeight: 900,
      letterSpacing: '-0.01em',
      color: BRAND_COLORS.violet,
    },
    h4: {
      fontSize: '2.2rem',
      fontWeight: 800,
      letterSpacing: '-0.01em',
      color: BRAND_COLORS.violet,
    },
    h5: {
      fontSize: '1.5rem',
      fontWeight: 800,
      color: BRAND_COLORS.violet,
    },
    h6: {
      fontSize: '1.3rem',
      fontWeight: 800,
      color: BRAND_COLORS.violet,
    },
    body1: {
      fontSize: '1.1rem',
      lineHeight: 1.6,
      fontWeight: 600,
    },
    body2: {
      fontSize: '1rem',
      lineHeight: 1.5,
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontSize: '1rem',
          fontWeight: 600,
          borderRadius: 8,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 10px 20px rgba(0, 0, 0, 0.1)',
          },
        },
        contained: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation3: {
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
            transform: 'translateY(-4px)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(128, 69, 255, 0.1)',
            },
            '&.Mui-focused': {
              boxShadow: '0 0 0 3px rgba(128, 69, 255, 0.1)',
            },
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(128, 69, 255, 0.04)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 0 rgba(17, 17, 17, 0.06)',
          borderBottom: '1px solid rgba(17, 17, 17, 0.08)',
        },
      },
    },
  },
});

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: BRAND_COLORS.violet,         // #44019A
      light: BRAND_COLORS.lightViolet,   // #8045FF
      dark: BRAND_COLORS.darkViolet,     // #270067
    },
    secondary: {
      main: BRAND_COLORS.lightViolet,    // #8045FF
      light: BRAND_COLORS.lightViolet,   // #8045FF
      dark: BRAND_COLORS.violet,         // #44019A
    },
    background: {
      default: '#262D38',
      paper: '#313846',
    },
    text: {
      primary: '#ffffff',
      secondary: BRAND_COLORS.lightGray, // #F4F4F4
    },
    success: {
      main: '#4CAF50',
      light: '#66BB6A',
      dark: '#2E7D32',
    },
    error: {
      main: BRAND_COLORS.pink,           // #FF4672
    },
    warning: {
      main: BRAND_COLORS.darkViolet,
    },
    info: {
      main: BRAND_COLORS.lightViolet,    // #8045FF
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: '3.5rem',
      fontWeight: 900,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '3rem',
      fontWeight: 900,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontSize: '2.2rem',
      fontWeight: 800,
      letterSpacing: '-0.01em',
    },
    h5: {
      fontSize: '1.5rem',
      fontWeight: 800,
    },
    h6: {
      fontSize: '1.3rem',
      fontWeight: 800,
    },
    body1: {
      fontSize: '1.1rem',
      lineHeight: 1.6,
      fontWeight: 600,
    },
    body2: {
      fontSize: '1rem',
      lineHeight: 1.5,
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontSize: '1rem',
          fontWeight: 600,
          borderRadius: 8,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 10px 20px rgba(0, 0, 0, 0.3)',
          },
        },
        contained: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation3: {
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
            transform: 'translateY(-4px)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(128, 69, 255, 0.2)',
            },
            '&.Mui-focused': {
              boxShadow: '0 0 0 3px rgba(128, 69, 255, 0.2)',
            },
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(128, 69, 255, 0.08)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 0 rgba(255, 255, 255, 0.04)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        },
      },
    },
  },
});

export { lightTheme, darkTheme };
